<template>
  <div>
    <p>
      <strong>Jeux de Montréal</strong>
      <b-button variant="link" size="sm" class="py-0 ml-2" @click="fetch" :disabled="busy">
        <i :class="`fa fa-fw ${busy ? 'fa-circle-notch fa-spin' : 'fa-redo'}`"></i>
      </b-button>
    </p>
    <p v-if="error" class="text-danger">
      {{ error }}
    </p>
    <b-overlay :show="busy">
      <address v-for="info in allowedBoroughs" :key="info.borough">
        <div><strong>{{ info.borough }}</strong></div>
        <div>
          {{ info.registration_count }}
          {{ trans('registration' + (info.registration_count > 1 || info.registration_count == 0 && lang == 'en' ? 's' : '')).toLowerCase() }}
          <b-button variant="link" size="sm" class="py-0" @click="onClickShowBorough(info.borough)">
            {{ trans('athletes') }}
            <i :class="`fa fa-fw fa-chevron-${show_borough == info.borough ? 'up' : 'down'}`"></i>
          </b-button>
        </div>
        <div v-if="show_borough == info.borough" class="mt-2 px-2">
          <div v-for="(s, index) in allSports" :key="s.sport+'.'+index" class="mb-2">
            <div v-if="info.map[s.sport] && s.limit_athletes > 0">
              <small class="d-block">
                {{ s.sport }} {{ info.map[s.sport].athletes }} / <strong>{{ s.limit_athletes }}</strong>
                <span v-if="info.map[s.sport].substitutes > 0" class="text-muted">
                  +{{ info.map[s.sport].substitutes }} sub
                </span>
              </small>
              <b-progress
                :variant="info.map[s.sport].variant"
                :value="info.map[s.sport].athletes"
                :max="s.limit_athletes"
                height="1.5em"
              ></b-progress>
            </div>
            <div v-if="info.map[s.sport] && !s.limit_athletes">
              <small class="d-block">
                {{ s.sport }} {{ info.map[s.sport].athletes }}
              </small>
              <b-progress
                :variant="info.map[s.sport].variant"
                striped
                :value="info.map[s.sport].athletes"
                :max="info.map[s.sport].athletes || 1"
                height="1.5em"
              ></b-progress>
            </div>
            <div v-if="!info.map[s.sport]" class="d-block">
              <small v-if="s.limit_athletes > 0">
                {{ s.sport }} (0 / <strong>{{ s.limit_athletes }}</strong>)
              </small>
              <small v-if="!s.limit_athletes" class="d-block">
                {{ s.sport }} (0)
              </small>
              <b-progress
                variant="success"
                :striped="!s.limit_athletes"
                :value="0"
                :max="1"
                height="1.5em"
              ></b-progress>
            </div>
          </div>
        </div>
      </address>
      <address v-for="info in allowedSports" :key="info.sport">
        <div>{{ info.sport }}</div>
        <div>
          <strong>{{ info.registration_count }}</strong>
          <span v-if="info.limit_athletes">/ {{ info.limit_athletes * jdm.all_boroughs.length }}</span>
          {{ trans('registration' + (info.registration_count > 1 || info.registration_count == 0 && lang == 'en' ? 's' : '')).toLowerCase() }}
          <b-button variant="link" size="sm" class="py-0" @click="onClickShowSport(info.sport)">
            {{ trans('athletes') }}
            <i :class="`fa fa-fw fa-chevron-${show_sport == info.sport ? 'up' : 'down'}`"></i>
          </b-button>
        </div>
        <div v-if="!!jdm && jdm.lock_sports_for_coaches.indexOf(info.sport) >= 0" class="mb-2">
          <small class="text-danger">{{ trans('registrations_are_closed_for_coaches') }}</small>
        </div>
        <div v-if="show_sport == info.sport" class="px-2 mt-2">
          <div v-for="(b, index) in jdm.all_boroughs" :key="b+'.'+index" class="mb-2">
            <div v-if="info.map[b] && info.limit_athletes > 0">
              <small class="d-block">
                {{ b }} {{ info.map[b].athletes }} / <strong>{{ info.limit_athletes }}</strong>
                <span v-if="info.map[b].substitutes > 0" class="text-muted">
                  +{{ info.map[b].substitutes }} sub
                </span>
              </small>
              <b-progress
                :variant="info.map[b].variant"
                :value="info.map[b].athletes"
                :max="info.limit_athletes"
                height="1.5em"
              ></b-progress>
            </div>
            <div v-if="info.map[b] && !info.limit_athletes">
              <small class="d-block">
                {{ b }} {{ info.map[b].athletes }}
              </small>
              <b-progress
                :variant="info.map[b].variant"
                striped
                :value="info.map[b].athletes"
                :max="info.map[b].athletes || 1"
                height="1.5em"
              ></b-progress>
            </div>
            <div v-if="!info.map[b]" class="d-block">
              <small v-if="info.limit_athletes > 0">
                {{ b }} (0 / <strong>{{ info.limit_athletes }}</strong>)
              </small>
              <small v-if="!info.limit_athletes" class="d-block">
                {{ b }} (0)
              </small>
              <b-progress
                variant="success"
                :striped="!info.limit_athletes"
                :value="0"
                :max="1"
                height="1.5em"
              ></b-progress>
            </div>
          </div>
        </div>
      </address>
      <address v-if="!!clubInfo">
        <div>
          <strong>{{ trans('club_access') }}</strong>
          <strong v-if="!!clubInfo.club_name">: {{ clubInfo.club_name }}</strong>
          <small class="text-muted" :title="trans('club_access_about')" v-b-tooltip.hover>
            <i class="fa fa-fw fa-info-circle"></i>
          </small>
        </div>
        <div class="mt-2" v-if="!!clubInfo.club_admins && clubInfo.club_admins.length > 0">
          <small v-for="(admin, index) in clubInfo.club_admins" :key="'admin'+index" class="d-block mb-1">
            <span class="d-block">{{ admin.name }}</span>
            <b-link :href="`mailto:${admin.email}`" class="d-block">{{ admin.email }}</b-link>
            <b-link v-if="!!admin.mobile" :href="`tel:${admin.mobile}`" class="d-block">{{ admin.mobile }}</b-link>
          </small>
        </div>
        <div class="mt-2">
          <b-button variant="outline-primary" size="sm" class="py-0" :disabled="busy" v-b-modal.club-permission-client-form-iframe-modal>
            <i class="fa fa-fw fa-edit"></i>
            {{ trans('modify') }}
          </b-button>
        </div>
      </address>
    </b-overlay>
    <client-form-iframe-modal
      id="club-permission-client-form-iframe-modal"
      :organization="organization"
      :upid="clubInfo ? clubInfo.recipient_form_upid : null"
      :urid="clubInfo ? clubInfo.recipient_urid || null : null"
      :title="trans('modify')"
      @hidden="fetch"
    ></client-form-iframe-modal>
  </div>
</template>

<script>
  import ClientFormIframeModal from './ClientFormIframeModal'

  export default {
    props: {
      organization: Object,
    },
    data() {
      return {
        trans: trans,
        lang: lang,
        busy: false,
        error: null,
        jdm: null,
        show_borough: null,
        show_sport: null,
      }
    },
    components: {
      ClientFormIframeModal,
    },
    computed: {
      allowedBoroughs() {
        if (!this.jdm) return []
        return _.map(this.jdm.allowed_boroughs || [], borough => {
          let registrations = this.getRegistrationsForBorough(borough)
          return {
            borough: borough,
            registration_count: registrations.length,
            map: _.reduce(registrations, (map, r) => {
              if (!map[r.sport]) {
                let [limit_athletes, limit_substitutes] = this.getLimitsForSport(r.sport)
                map[r.sport] = {
                  athletes: 0,
                  substitutes: 0,
                  limit_athletes: limit_athletes,
                  limit_substitutes: limit_substitutes,
                  variant: 'success'
                }
              }
              if (r.participant_type && r.participant_type.toLowerCase().indexOf('sub') === 0) {
                map[r.sport].substitutes += 1
              } else {
                map[r.sport].athletes += 1
                if (map[r.sport].limit_athletes &&
                  map[r.sport].athletes > map[r.sport].limit_athletes)
                  map[r.sport].variant = 'danger'
              }
              return map
            }, {}),
          }
        })
      },
      allowedSports() {
        if (!this.jdm) return []
        return _.map(this.jdm.allowed_sports || [], sport => {
          let registrations = this.getRegistrationsForSport(sport)
          let [limit_athletes, limit_substitutes] = this.getLimitsForSport(sport)
          return {
            sport: sport,
            registration_count: registrations.length,
            limit_athletes: limit_athletes,
            limit_substitutes: limit_substitutes,
            map: _.reduce(registrations, (map, r) => {
              if (!map[r.borough]) {
                map[r.borough] = {
                  athletes: 0,
                  substitutes: 0,
                  variant: 'success'
                }
              }
              if (r.participant_type && r.participant_type.toLowerCase().indexOf('sub') === 0) {
                map[r.borough].substitutes += 1
              } else {
                map[r.borough].athletes += 1
                if (limit_athletes &&
                  map[r.borough].athletes > limit_athletes)
                  map[r.borough].variant = 'danger'
              }
              return map
            }, {}),
          }
        })
      },
      allSports() {
        return _.map(this.jdm.all_sports || [], sport => {
          let [limit_athletes, limit_substitutes] = this.getLimitsForSport(sport)
          return {
            sport: sport,
            limit_athletes: limit_athletes,
            limit_substitutes: limit_substitutes,
          }
        })
      },
      clubInfo() {
        if (!this.jdm || this.allowedSports.length > 0 || this.allowedBoroughs > 0) return null
        return !!this.jdm &&
          !!this.jdm.client_account_permission &&
          !!this.jdm.client_account_permission.recipient_form_upid ?
          this.jdm.client_account_permission :
          null
      },
    },
    methods: {
      onClickShowBorough(borough) {
        if (this.show_borough == borough) {
          this.show_borough = null
        } else {
          this.show_borough = borough
        }
      },
      onClickShowSport(sport) {
        if (this.show_sport == sport) {
          this.show_sport = null
        } else {
          this.show_sport = sport
        }
      },
      getRegistrationsForBorough(borough) {
        if (!this.jdm) return []
        return _.reduce(this.jdm.registrations, (registrations, registration) => {
          if (registration.borough == borough) registrations.push(_.clone(registration))
          return registrations
        }, [])
      },
      getRegistrationsForSport(sport) {
        if (!this.jdm) return []
        return _.reduce(this.jdm.registrations, (registrations, registration) => {
          if (registration.sport == sport) registrations.push(registration)
          return registrations
        }, [])
      },
      getLimitsForSport(sport) {
        if (!this.jdm) return [null, null]
        return _.reduce(this.jdm.registration_forms, (limits, form) => {
          if (form.sport == sport) limits = [form.limit_athletes, form.limit_substitutes]
          return limits
        }, [null, null])
      },
      fetch() {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/client/jdm_dashboard`
        axios.get(url)
        .then(response => {
          this.busy = false
          this.jdm = response.data
          this.$emit('jdm', this.jdm)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window.jdmInformation = this
      this.fetch()
    },
  }
</script>